import React, { useEffect, useState, useMemo } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useToastMessage } from "../../../components/ToastMessage/ToastMessage";
import { Toaster } from "@fluentui/react-components";
import { FormLoader } from "../../../components/Loader/FormLoader";
import { TableListLoader } from "../../../components/Loader/TableListLoader";
import { configFm } from "../../../types";
import { AppDispatch, RootState } from "../../../redux/store";

import FormAreaWrapper from "../../../components/Common/FormAreaWrapper";
import TableListWrapper from "../../../components/Common/TableListWrapper";

import {
  getVulnerabilityDetailById,
  getVulnerabilityListItems,
} from "../../../redux/Client/vulnerability";
import VulnerabilityFormWrapper from "../VulnerabilityFormWrapper";
import NoDataFound from "../../../components/NoDataFound/noDataFound";
import {
  getPartnerVulnerabilityDetailById,
  getPartnerVulnerabilityListItems,
} from "../../../redux/Client/Partner/partnerVulnerability";
const FORM_CONTEXT = "VULNERABILITY";

function PartnerVulnerabilityDetailPage(props: any) {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { clientId, selectedItemId, action } = useParams<{
    clientId: string;
    selectedItemId?: string;
    action?: string;
  }>();
  const API_BASE_PATH = `/clients/${clientId}/partner/vulnerability`;
  const { showToast, toasterId } = useToastMessage();
  const { activeItem, listItems, loading, activeItemloading } = useSelector(
    (state: RootState) => state.partnerVulnerability
  );

  const activeClient = useSelector((state: RootState) => state.activeClient);

  const formSettingsObj = useSelector((state: RootState) => state.formSettings);
  const [configForm, setConfigForm] = useState<configFm>();
  const [currentRecord, setCurrentRecord] = useState(1);
  useEffect(() => {
    if (activeClient.basicDetails.associated_with) {
      dispatch(
        getPartnerVulnerabilityListItems(
          activeClient.basicDetails.associated_with
        )
      );
    }
  }, [dispatch, activeClient.basicDetails.associated_with]);

  useEffect(() => {
    if (listItems.length > 0 && selectedItemId) {
      dispatch(getPartnerVulnerabilityDetailById(selectedItemId));
    }
  }, [listItems, selectedItemId, dispatch]);

  // A URL with an action but no id (e.g. the Partner sidebar's "Vulnerability"
  // link, .../partner/vulnerability/view) never dispatches
  // getPartnerVulnerabilityDetailById above (it requires selectedItemId), so
  // `activeItem` in the store is left holding whatever it last was --
  // including a just-deleted record, even after `listItems` itself has
  // correctly refreshed to empty. Redirect to a real record's own id (same
  // rule PartnerVulnerabilityHomePage already uses for the bare list path) so
  // the form always renders a genuinely selected, current record.
  useEffect(() => {
    if (
      action !== "add" &&
      !selectedItemId &&
      !loading &&
      listItems.length > 0
    ) {
      navigate(
        `${API_BASE_PATH}/${listItems[0].vulnerability_id}/${action || "view"}`,
        { replace: true },
      );
    }
  }, [selectedItemId, listItems, loading, action, API_BASE_PATH, navigate]);

  useEffect(() => {
    if (formSettingsObj.data?.formConfig) {
      const configObj = formSettingsObj.data.formConfig.find(
        (item: any) => item.context === FORM_CONTEXT
      );
      setConfigForm(configObj);
    }
  }, [formSettingsObj.data]);

  const loadData = async (
    dispatch: AppDispatch,
    id?: string
  ): Promise<void> => {
    if (activeClient.basicDetails.associated_with) {
      await dispatch(
        getPartnerVulnerabilityListItems(
          activeClient.basicDetails.associated_with
        )
      );
      if (id) {
        await dispatch(getPartnerVulnerabilityDetailById(id));
      }
    }
  };
  const hasListItems = useMemo(() => listItems.length > 0, [listItems]);
  return (
    <>
      <Toaster toasterId={toasterId} position="top-end" timeout={5000} />
      <div className="admin_container_flex">
        {action && (action === "add" || selectedItemId) ? (
          activeItemloading ? (
            <FormLoader />
          ) : (
            <VulnerabilityFormWrapper
              activeCustomerId={activeClient.basicDetails.associated_with}
              mode={action}
              dataList={listItems}
              context={FORM_CONTEXT}
              notify={showToast}
              reloadData={loadData}
              section="vulnerability"
              apiPath={API_BASE_PATH}
              selectedRowItem={activeItem}
              activeRow={currentRecord}
              module="partner"
            />
          )
        ) : (
          action &&
          !loading &&
          listItems.length === 0 && <NoDataFound />
        )}

        {loading ? (
          <TableListLoader rows={8} columns={6} />
        ) : (
          hasListItems &&
          action &&
          listItems.length > 0 && (
            <TableListWrapper
              data={listItems}
              context={FORM_CONTEXT}
              selectedRowItem={activeItem}
              onRowAction={(action, row) =>
                navigate(
                  `${API_BASE_PATH}/${row?.vulnerability_id}/${
                    action || "view"
                  }`
                )
              }
              currentRecord={(index) => setCurrentRecord(index)}
            />
          )
        )}
      </div>
    </>
  );
}

export default PartnerVulnerabilityDetailPage;
